import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import EvaluationNavigation from '../../components/EvaluationNavigation';
import theme from '../../theme/theme';
import ButtonComponent from '../../components/ButtonComponent';
import MySpinner from '../../components/MySpinner';
import { getEvaluationItemsAsync, createNewEvaluationAsync } from '../../services/evaluationService';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { useNavigate } from 'react-router-dom';
import { routePath } from '../../routes/routePath';
import { Modal } from 'react-bootstrap';

const CreateNewEvaluation = () => {
  const navigate = useNavigate();
  const { hierId } = useParams();
  const { userInfo } = useAuth();
  const empId = userInfo?.empId;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [evaluationItems, setEvaluationItems] = useState(null);
  const [subordinateInfo, setSubordinateInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [ratings, setRatings] = useState({});

  useEffect(() => {
    if (!hierId) return;

    const fetchEvaluationItems = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await getEvaluationItemsAsync(hierId);
        const items = response.evaluationItems || [];
        setEvaluationItems(items);
        setSubordinateInfo(response.subordinate);

        // Initialize ratings with 5 (ممتاز) by default
        const initialRatings = {};
        items.forEach(item => {
          initialRatings[item.evaluationItemId] = 5;
        });
        setRatings(initialRatings);
      } catch (err) {
        setError(err.errorMessages || 'حدث خطأ الرجاء المحاولة لاحقا.');
      } finally {
        setLoading(false);
      }
    };

    fetchEvaluationItems();
  }, [hierId]);

  const handleRatingChange = (evaluationItemId, newRating) => {
    setRatings(prev => ({
      ...prev,
      [evaluationItemId]: parseInt(newRating, 10)
    }));
  };

  const SubmitEvaluation = async () => {
    const unratedItems = evaluationItems.filter(item => !ratings[item.evaluationItemId]);
    if (unratedItems.length > 0) {
      setModalMessage('⚠️ الرجاء تقييم جميع العناصر قبل الإرسال');
      setShowModal(true);
      return;
    }

    setIsSubmitting(true);
    try {
      const items = evaluationItems.map((item) => ({
        evaluationItemId: item.evaluationItemId,
        rating: ratings[item.evaluationItemId],
      }));
      const payload = { items };
      await createNewEvaluationAsync(hierId, payload);
      setModalMessage('تم التقييم بنجاح');
      setShowModal(true);
    } catch (error) {
      setModalMessage(error.message);
      setShowModal(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRedirection = () => {
    navigate(routePath.subordinateEvaluation);
  };

  // Progress calculations
  const totalScore = Object.values(ratings).reduce((sum, r) => sum + (r || 0), 0);
  const maxScore = evaluationItems ? evaluationItems.length * 5 : 1;
  const percentage = evaluationItems ? Math.round((totalScore / maxScore) * 100) : 0;
  const ratedCount = Object.values(ratings).filter(r => r !== '').length;
  const totalCount = evaluationItems ? evaluationItems.length : 0;

  if (loading) return <Layout><MySpinner color={theme.colors.primary} /></Layout>;
  if (error) return (
    <Layout>
      <EvaluationNavigation />
      <div className="alert alert-danger text-center my-5" style={{ maxWidth: '600px', margin: '2rem auto' }}>
        <strong>خطأ:</strong> {error}
      </div>
    </Layout>
  );
  if (!evaluationItems || evaluationItems.length === 0) return (
    <Layout>
      <EvaluationNavigation />
      <div style={styles.emptyState}>
        <div style={styles.emptyIcon}>📋</div>
        <p style={styles.emptyText}>لم يتم العثور على عناصر التقييم</p>
      </div>
    </Layout>
  );

  return (
    <Layout>
      <EvaluationNavigation />

      <div style={styles.container}>
        {/* Employee Info Card */}
        <div style={styles.employeeCard}>
          <div style={styles.employeeHeader}>
            <h2 style={styles.employeeTitle}>تقييم موظف جديد</h2>
            <span style={styles.newBadge}>✨ جديد</span>
          </div>

          <div style={styles.employeeBody}>
            <div style={styles.employeeMainInfo}>
              <h3 style={styles.employeeName}>{subordinateInfo.name}</h3>
            </div>

            <div style={styles.employeeDetailsGrid}>
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>القسم:</span>
                <span style={styles.detailValue}>{subordinateInfo.deptName}</span>
              </div>
              <div style={styles.detailItem}>
                <span style={styles.detailLabel}>المسمى الوظيفي:</span>
                <span style={styles.detailValue}>{subordinateInfo.positionTitle}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Indicator - Sticky */}
        <div style={styles.progressCard}>
          <div style={styles.progressHeader}>
            <span style={styles.progressLabel}>التقدم في التقييم</span>
            <div style={styles.progressStats}>
              <span style={styles.progressCount}>
                {ratedCount} / {totalCount}
              </span>
              <span style={{
                ...styles.progressPercentage,
                color: percentage === 100 ? '#4caf50' : '#ff9800'
              }}>
                {percentage}%
              </span>
            </div>
          </div>
          <div style={styles.progressBar}>
            <div
              style={{
                ...styles.progressFill,
                width: `${percentage}%`,
                backgroundColor: percentage === 100 ? '#4caf50' : '#ff9800'
              }}
            />
          </div>
        </div>

        {/* Evaluation Items */}
        <div style={styles.evaluationSection}>
          <h3 style={styles.sectionTitle}>
            <span style={styles.sectionIcon}>📝</span>
            عناصر التقييم
          </h3>

          <div style={styles.itemsContainer}>
            {evaluationItems.map((item, index) => {
              const isRated = ratings[item.evaluationItemId] !== '';
              return (
                <div
                  key={item.evaluationItemId}
                  style={{
                    ...styles.evaluationItem,
                    ...(isRated ? styles.evaluationItemRated : {})
                  }}
                >
                  <div style={styles.itemHeader}>
                    <span style={styles.itemNumber}>#{index + 1}</span>
                    <h4 style={styles.itemName}>{item.itemName}</h4>
                    {isRated && <span style={styles.ratedBadge}>✓</span>}
                  </div>

                  <div style={styles.ratingContainer}>
                    <label style={styles.ratingLabel}>التقدير:</label>
                    <select
                      id={`rating-${item.evaluationItemId}`}
                      value={ratings[item.evaluationItemId] || ''}
                      onChange={(e) => handleRatingChange(item.evaluationItemId, e.target.value)}
                      style={{
                        ...styles.ratingSelect,
                        ...(ratings[item.evaluationItemId] === '' ? styles.ratingSelectEmpty : {})
                      }}
                    >
                      <option value="">-- اختر التقدير --</option>
                      <option value={5}>ممتاز (5)</option>
                      <option value={4}>جيد جدا (4)</option>
                      <option value={3}>جيد (3)</option>
                      <option value={2}>مرضي (2)</option>
                      <option value={1}>غير مرضي (1)</option>
                    </select>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Submit Button */}
        <div style={styles.submitContainer}>
          {isSubmitting ? (
            <button disabled style={styles.submittingButton}>
              <MySpinner color={theme.colors.light} />
              <span style={{ marginRight: '0.5rem' }}>جاري إرسال التقييم...</span>
            </button>
          ) : (
            <ButtonComponent
              onClick={SubmitEvaluation}
              text="✅ إرسال التقييم"
              className="primary-bg"
              style={styles.submitButton}
            />
          )}
        </div>
      </div>

      {/* Success/Error Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        dialogClassName="custom-modal"
      >
        <Modal.Header style={styles.modalHeader}>
          <Modal.Title style={styles.modalTitle}>
            {modalMessage.includes('نجاح') ? '✅' : '⚠️'} {modalMessage}
          </Modal.Title>
        </Modal.Header>
        <Modal.Footer style={styles.modalFooter}>
          <ButtonComponent
            text={modalMessage.includes('نجاح') ? 'إغلاق والعودة' : 'حسناً'}
            className="primary-bg"
            onClick={modalMessage.includes('نجاح') ? handleRedirection : () => setShowModal(false)}
            style={styles.modalButton}
          />
        </Modal.Footer>
      </Modal>
    </Layout>
  );
};

const styles = {
  container: {
    maxWidth: '900px',
    margin: '0 auto',
    padding: '2rem',
    direction: 'rtl'
  },
  employeeCard: {
    backgroundColor: 'white',
    borderRadius: '16px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
    overflow: 'hidden',
    marginBottom: '1.5rem'
  },
  employeeHeader: {
    backgroundColor: '#f8f9fa',
    padding: '1.5rem',
    borderBottom: '2px solid #e0e0e0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  employeeTitle: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#1a1a1a',
    margin: 0
  },
  newBadge: {
    padding: '0.5rem 1rem',
    borderRadius: '20px',
    backgroundColor: '#4caf50',
    color: 'white',
    fontSize: '0.85rem',
    fontWeight: 'bold'
  },
  employeeBody: {
    padding: '2rem'
  },
  employeeMainInfo: {
    textAlign: 'center',
    marginBottom: '1.5rem'
  },
  employeeName: {
    fontSize: '1.75rem',
    fontWeight: 'bold',
    color: '#1a1a1a',
    margin: 0
  },
  employeeDetailsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1rem'
  },
  detailItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    padding: '1rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '12px',
    border: '1px solid #e0e0e0'
  },
  detailLabel: {
    fontSize: '0.85rem',
    color: '#666',
    fontWeight: '500'
  },
  detailValue: {
    fontSize: '1rem',
    color: '#1a1a1a',
    fontWeight: 'bold'
  },

  // Sticky progress card
  progressCard: {
    backgroundColor: 'white',
    borderRadius: '16px',
    padding: '1.5rem',
    marginBottom: '2rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    border: '1px solid #e0e0e0',
    position: 'sticky',
    top: '1rem',
    zIndex: 100
  },
  progressHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem'
  },
  progressLabel: {
    fontSize: '1rem',
    fontWeight: '600',
    color: '#666'
  },
  progressStats: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem'
  },
  progressCount: {
    fontSize: '1.1rem',
    fontWeight: 'bold',
    color: '#1a1a1a'
  },
  progressPercentage: {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    minWidth: '55px',
    textAlign: 'left'
  },
  progressBar: {
    width: '100%',
    height: '12px',
    backgroundColor: '#f0f0f0',
    borderRadius: '6px',
    overflow: 'hidden'
  },
  progressFill: {
    height: '100%',
    transition: 'width 0.3s ease, background-color 0.3s ease',
    borderRadius: '6px'
  },

  evaluationSection: {
    marginBottom: '2rem'
  },
  sectionTitle: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#1a1a1a',
    marginBottom: '1.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem'
  },
  sectionIcon: {
    fontSize: '1.75rem'
  },
  itemsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  evaluationItem: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '1.5rem',
    border: '2px solid #e0e0e0',
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
    transition: 'all 0.3s ease'
  },
  evaluationItemRated: {
    borderColor: '#4caf50',
    backgroundColor: '#f1f8f4'
  },
  itemHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '1rem'
  },
  itemNumber: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: '#f0f0f0',
    color: '#666',
    fontSize: '0.9rem',
    fontWeight: 'bold'
  },
  itemName: {
    fontSize: '1.1rem',
    fontWeight: '600',
    color: '#1a1a1a',
    margin: 0,
    flex: 1
  },
  ratedBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    backgroundColor: '#4caf50',
    color: 'white',
    fontSize: '0.9rem',
    fontWeight: 'bold'
  },
  ratingContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem'
  },
  ratingLabel: {
    fontSize: '1rem',
    color: '#666',
    fontWeight: '500',
    minWidth: '60px'
  },
  ratingSelect: {
    flex: 1,
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    border: '2px solid #e0e0e0',
    fontSize: '1rem',
    fontWeight: '600',
    backgroundColor: 'white',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    outline: 'none'
  },
  ratingSelectEmpty: {
    borderColor: '#ff9800',
    backgroundColor: '#fff3e0'
  },
  submitContainer: {
    textAlign: 'center',
    marginTop: '2rem'
  },
  submitButton: {
    minWidth: '250px',
    padding: '1rem 2rem',
    fontSize: '1.1rem',
    fontWeight: 'bold'
  },
  submittingButton: {
    minWidth: '250px',
    padding: '1rem 2rem',
    fontSize: '1.1rem',
    fontWeight: 'bold',
    backgroundColor: '#ccc',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'not-allowed',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  emptyState: {
    textAlign: 'center',
    padding: '4rem 2rem',
    backgroundColor: '#fafafa',
    borderRadius: '16px',
    border: '2px dashed #e0e0e0',
    maxWidth: '600px',
    margin: '2rem auto'
  },
  emptyIcon: {
    width: '80px',
    height: '80px',
    margin: '0 auto 1.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    backgroundColor: '#fff3e0',
    color: '#ff9800',
    fontSize: '2.5rem'
  },
  emptyText: {
    fontSize: '1.1rem',
    color: '#666',
    margin: 0
  },
  modalHeader: {
    borderBottom: '2px solid #e0e0e0',
    padding: '1.5rem'
  },
  modalTitle: {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    textAlign: 'center',
    width: '100%'
  },
  modalFooter: {
    borderTop: 'none',
    padding: '1rem 1.5rem',
    justifyContent: 'center'
  },
  modalButton: {
    minWidth: '200px'
  }
};

export default CreateNewEvaluation;